import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { getDocumentTitle } from "utils/helpers";
import { getLeaveApplications } from "../services/LeaveApplicationService";
import type { LeaveApplication } from "../types/leave-application.types";
import {
  LeaveApplicationStatus,
  leaveApplicationStatusMap,
} from "../constants/leave-application.constants";
import {
  leaveApplicationEmployeeLabel,
  leaveTypeLabel,
} from "../utils/leaveApplicationDisplay";
import LeaveApplicationListFilters from "./LeaveApplicationListFilters";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  LeaveApplicationActionModals,
  type LeaveApplicationAction,
} from "../components/LeaveApplicationActionModals";

const LeaveApplicationList = () => {
  document.title = getDocumentTitle("Leave applications");
  const { can } = useAccess();
  const canRead = can(Permissions.LEAVE_APPLICATION.READ);
  const canCreate = can(Permissions.LEAVE_APPLICATION.CREATE);
  const canApprove = can(Permissions.LEAVE_APPLICATION.APPROVE);
  const canReject = can(Permissions.LEAVE_APPLICATION.REJECT);
  const canDelete = can(Permissions.LEAVE_APPLICATION.DELETE);
  const canCancel = can(Permissions.LEAVE_APPLICATION.CANCEL);
  const tableRef = useRef<() => void>(null);
  const [statusFilter, setStatusFilter] = useState<LeaveApplicationStatus | "">(
    "",
  );
  const [leaveTypeId, setLeaveTypeId] = useState<number | null>(null);
  const [employeeId, setEmployeeId] = useState<number | null>(null);

  const [actionRecord, setActionRecord] = useState<LeaveApplication | null>(
    null,
  );
  const [action, setAction] = useState<LeaveApplicationAction | null>(null);

  const openApprove = useCallback((r: LeaveApplication) => {
    setActionRecord(r);
    setAction("approve");
  }, []);
  const openReject = useCallback((r: LeaveApplication) => {
    setActionRecord(r);
    setAction("reject");
  }, []);
  const openDelete = useCallback((r: LeaveApplication) => {
    setActionRecord(r);
    setAction("delete");
  }, []);
  const openCancel = useCallback((r: LeaveApplication) => {
    setActionRecord(r);
    setAction("cancel");
  }, []);

  const requestParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      leaveTypeId: leaveTypeId ?? undefined,
      employeeId: employeeId ?? undefined,
    }),
    [statusFilter, leaveTypeId, employeeId],
  );

  const columns: ColumnDef<LeaveApplication>[] = useMemo(
    () => [
      IndexColumn<LeaveApplication>(),
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        accessorFn: (row) => leaveApplicationEmployeeLabel(row),
        cell: ({ row }) => {
          const label = leaveApplicationEmployeeLabel(row.original);
          return canRead ? (
            <Link
              to={`/hr-management/leave-applications/${row.original.id}`}
              className="fw-medium link"
            >
              {label}
            </Link>
          ) : (
            <span className="text-body">{label}</span>
          );
        },
      },
      {
        header: "Leave type",
        id: "leaveType",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="text-body">{leaveTypeLabel(row.original)}</span>
        ),
      },
      {
        header: "Period",
        id: "period",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { fromDate, toDate, isHalfDay, totalDays } = row.original;
          if (!fromDate && !toDate)
            return <span className="text-muted">—</span>;
          const days =
            totalDays != null
              ? totalDays
              : fromDate && toDate
                ? Math.round(
                    (new Date(`${toDate}T12:00:00`).getTime() -
                      new Date(`${fromDate}T12:00:00`).getTime()) /
                      86_400_000,
                  ) + 1
                : null;
          const daysLabel =
            days == null
              ? null
              : days === 0.5 || isHalfDay
                ? "0.5 days"
                : days === 1
                  ? "1 day"
                  : `${days} days`;
          return (
            <div>
              <div className="text-body">
                <DateOnly date={fromDate} />
                {!isHalfDay && toDate && toDate !== fromDate ? (
                  <>{" → "}<DateOnly date={toDate} /></>
                ) : null}
              </div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {isHalfDay ? (
                  <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                    Half Day
                  </span>
                ) : null}
                {daysLabel ? (
                  <span className="badge bg-light text-muted border">
                    {daysLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={leaveApplicationStatusMap}
          />
        ),
      },
      {
        header: "Updated",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.updated_at ? (
            <DateTime date={row.original.updated_at} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const base = `/hr-management/leave-applications/${r.id}`;
          const isPending = r.status === LeaveApplicationStatus.PENDING;
          const isApproved = r.status === LeaveApplicationStatus.APPROVED;
          const showApprove = isPending && canApprove;
          const showReject = isPending && canReject;
          const showDelete = isPending && canDelete;
          const showCancel = isApproved && canCancel;
          const hasMenu =
            canRead || showApprove || showReject || showDelete || showCancel;
          if (!hasMenu) {
            return <span className="text-muted small">—</span>;
          }
          const hasActionDivider =
            canRead && (showApprove || showReject || showDelete || showCancel);
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Leave application actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canRead ? (
                  <DropdownItem tag={Link} to={base}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {hasActionDivider ? <DropdownItem divider /> : null}
                {showApprove ? (
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openApprove(r);
                    }}
                  >
                    <i className="ri-check-line align-bottom me-2 text-muted" />
                    Approve…
                  </DropdownItem>
                ) : null}
                {showReject ? (
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openReject(r);
                    }}
                  >
                    <i className="ri-close-line align-bottom me-2 text-muted" />
                    Reject…
                  </DropdownItem>
                ) : null}
                {showDelete ? (
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openDelete(r);
                    }}
                  >
                    <i className="ri-delete-bin-line align-bottom me-2 text-muted" />
                    Delete…
                  </DropdownItem>
                ) : null}
                {showCancel ? (
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      openCancel(r);
                    }}
                  >
                    <i className="ri-forbid-line align-bottom me-2 text-muted" />
                    Cancel…
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      canRead,
      canApprove,
      canReject,
      canDelete,
      canCancel,
      openApprove,
      openReject,
      openDelete,
      openCancel,
    ],
  );

  return (
    <div className="page-content">
      <LeaveApplicationActionModals
        record={actionRecord}
        action={action}
        onClose={() => setAction(null)}
        onSuccess={() => {
          setAction(null);
          tableRef.current?.();
        }}
      />

      <Container fluid>
        <BreadCrumb title="Leave applications" pageTitle="HR Management" />
        <Card>
          <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="card-title mb-1">Leave applications</h5>
              <p className="text-muted small mb-0">
                Request and track leave. Approving writes attendance (LEAVE) for
                each day in range; balances remain a separate concern.
              </p>
            </div>
            {canCreate ? (
              <Link
                to="/hr-management/leave-applications/new"
                className="btn btn-primary"
              >
                <i className="ri-add-line align-bottom me-1" />
                New application
              </Link>
            ) : null}
          </CardHeader>
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              fetchApi={getLeaveApplications}
              requestParams={requestParams}
              pageSize={20}
              globalSearch={false}
              refreshRef={tableRef}
              divClass="table-responsive table-card mb-0"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
              toolbarRight={
                <LeaveApplicationListFilters
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                  leaveTypeId={leaveTypeId}
                  onLeaveTypeChange={setLeaveTypeId}
                  showEmployeeFilter={canApprove || canReject}
                  employeeId={employeeId}
                  onEmployeeChange={setEmployeeId}
                />
              }
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default LeaveApplicationList;
